/**
 * Region-aware navigation helpers.
 *
 * Section list pages that are scoped to a DR region. Detail routes nested under
 * any of these (e.g. `/customers/all-tenants/:id/:tabId`) carry a region-specific
 * entity id that is invalid in another region, so on a region switch they
 * collapse back to their section's list page. Pure list pages map to themselves.
 *
 * Exceptions that stay put instead of collapsing are listed in
 * `REGION_SWITCH_STAY_PATHS`.
 *
 * Keep in sync with the `exact` section routes in `src/routes.js` — add a base
 * path here whenever a new region-scoped section is introduced.
 */
export const REGION_SWITCH_BASE_PATHS = [
  '/customers/all-tenants',
  '/customers/networks',
  '/customers/applications',
  '/customers/cancellations',
  '/interactions/satisfaction-board',
  '/interactions/qbrs-board',
  '/interactions/maintenance-windows',
  '/interactions/sentiment-board',
  '/cs/agent-rollout',
  '/cs/versions-activity-log',
  '/cs/network-rollout',
  '/cs/edges',
  '/cs/cloud-management-log',
  '/product/ff-activity-log',
  '/product/features-adoption',
  '/arr/arr-activity'
] as const;

/**
 * Exceptions to the collapse rule: detail routes under these keep their path on
 * a region switch, and `setRegion` carries their router state over. The page
 * itself must re-resolve its entity in the new region. The FF Assigned/
 * Not-Assigned page re-fetches its flag by `key`, the flag's cross-region
 * identity.
 */
export const REGION_SWITCH_STAY_PATHS = ['/product/feature-flag'] as const;

const isUnderBase = (pathname: string, base: string): boolean =>
  pathname === base || pathname.startsWith(`${base}/`);

/**
 * Whether `pathname` is a stay-put exception on a region switch.
 *
 * @param pathname - The current location pathname (no query string).
 */
export const isRegionSwitchStayPath = (pathname: string): boolean =>
  REGION_SWITCH_STAY_PATHS.some(base => isUnderBase(pathname, base));

/**
 * Resolves the path to navigate to after a DR region switch so the user keeps
 * their context: list pages and `REGION_SWITCH_STAY_PATHS` stay put, other
 * detail pages fall back to their section's list page, and non-region pages
 * are left unchanged.
 *
 * @param pathname - The current location pathname (no query string).
 * @returns The region-safe target pathname.
 */
export const getRegionSwitchPath = (pathname: string): string => {
  if (isRegionSwitchStayPath(pathname)) {
    return pathname;
  }

  const match = REGION_SWITCH_BASE_PATHS.filter(base =>
    isUnderBase(pathname, base)
  ).sort((a, b) => b.length - a.length)[0];

  return match ?? pathname;
};
